import Const from '../config/const';

/**
 * Front-channel single logout.
 *
 * An application's session is a cookie on its own host, so only the browser can
 * end it — a server-to-server call can sign a user out of every device, but not
 * out of one device's session in one app. So signing out walks the browser
 * through each connected application's logout URL, one hop at a time, and the
 * queue of remaining applications is parked in sessionStorage because each hop
 * is a full page load.
 *
 * Every app's contract is the one linentech already implements: `?from_sso=true`
 * means "the portal has already ended the SSO session, just end yours and come
 * back to SSO with ?action=logout". App.tsx picks the chain up from there.
 *
 * Deliberately not iframes or image beacons: those send the app's cookie as a
 * third-party cookie, which Chrome and Safari now block, so the logout would
 * silently do nothing.
 */

const STORAGE_KEY = 'sso_logout_chain';

/**
 * Where to send the browser once every product has been visited.
 *
 * Parked here rather than echoed through the hops so that adding it costs no
 * change to any product's logout contract — only the initiator names it, and
 * only the portal reads it back.
 *
 * Absent means "land on the portal's own login page", which is right for a
 * product that has no login page of its own (LinenDesk) and for a sign-out
 * started in the portal itself.
 */
const RETURN_KEY = 'sso_logout_return';

/**
 * Const keys holding client-app logout URLs, in visiting order. Adding an app
 * here is all that is needed to include it in single logout — provided its
 * REACT_APP_*_LOGOUT_URL is configured for the environment.
 */
const LOGOUT_URL_KEYS: readonly string[] = ['linentechLogout', 'linendeskLogout', 'linencrewLogout'];

const isUsableUrl = (key: string): boolean => /^https?:\/\//i.test(String(Const[key] ?? ''));

/**
 * A return target is only honoured when it points at an origin this portal
 * already redirects to. Anything else is an open redirect: the URL arrives in a
 * query string, so without this a crafted logout link could bounce a signed-out
 * user anywhere and still look like it came from us.
 */
const isAllowedReturnUrl = (candidate: string): boolean => {
  let target: URL;
  try {
    target = new URL(candidate);
  } catch {
    return false;
  }

  if (target.protocol !== 'http:' && target.protocol !== 'https:') {
    return false;
  }

  const allowed = new Set<string>([window.location.origin]);
  Object.keys(Const).forEach((key) => {
    const value = String(Const[key] ?? '');
    if (/^https?:\/\//i.test(value)) {
      try {
        allowed.add(new URL(value).origin);
      } catch {
        // Not a parseable URL — nothing to allow.
      }
    }
  });

  return allowed.has(target.origin);
};

/** Consumes the parked return target — single use, like the queue itself. */
export const consumeLogoutReturn = (): string | null => {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
  } catch {
    return null;
  }

  return stored && isAllowedReturnUrl(stored) ? stored : null;
};

/**
 * True once this page load has committed to a hop.
 *
 * The portal's logout effect runs more than once per page load, and the second
 * pass used to find the queue one entry shorter, conclude the chain was
 * finished, and client-side navigate to the login page — which cancelled the
 * cross-origin navigation the first pass had just started. That is why signing
 * out of one app left the others signed in.
 *
 * Module scope is exactly the right lifetime: every hop is a full page load, so
 * this resets on its own for the next one.
 */
let hopInFlight = false;

/**
 * null means no sign-out is in progress. An EMPTY ARRAY is different: it means a
 * chain is running and the last application has just reported back. Collapsing
 * those two states loops forever — the final hop would look like a fresh logout
 * and start the chain over.
 */
const readQueue = (): string[] | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((k) => typeof k === 'string') : [];
  } catch {
    // Private mode, storage disabled, or corrupt value: treat as no chain, so
    // logout degrades to ending the SSO session only.
    return null;
  }
};

/** Stores the queue, empty included — the key's presence is the "in progress" flag. */
const writeQueue = (keys: string[]): boolean => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(keys));
    return true;
  } catch {
    return false;
  }
};

/** True while a sign-out is part-way through the applications. */
export const isLogoutChainActive = (): boolean => readQueue() !== null;

export const clearLogoutChain = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored to clear.
  }
};

/**
 * Sends the browser to the next application's logout, or reports that there are
 * none left so the caller can land on the login page.
 *
 * @param application the `?application=` value to echo through the hops, so the
 *        login form can send the user back where they came from after re-login
 * @returns false when the chain is finished (or could not run at all)
 */
export const advanceLogoutChain = (application?: string | null): boolean => {
  // A hop is already navigating; report "still going" so the caller waits
  // instead of consuming the queue a second time.
  if (hopInFlight) {
    return true;
  }

  const queue = readQueue() ?? [];
  const nextKey = queue.shift();

  if (!nextKey) {
    clearLogoutChain();
    return false;
  }

  // Persist the shortened queue BEFORE navigating: the assignment below ends
  // this page, and a queue written afterwards would never be stored.
  writeQueue(queue);

  const url = String(Const[nextKey]);
  const params = new URLSearchParams({ from_sso: 'true' });
  if (application) {
    params.set('application', application);
  }

  hopInFlight = true;
  window.location.href = `${url}${url.includes('?') ? '&' : '?'}${params.toString()}`;
  return true;
};

/**
 * Starts a sign-out across every configured application.
 *
 * @param initiatingApplication an app that has already ended its own session
 *        (it redirected here to log out) and so does not need visiting again
 * @param application the `?application=` value to carry through the hops
 * @param returnTo where to send the browser when the chain finishes; ignored
 *        unless it points at a known origin
 * @returns false when there is nothing to visit — caller lands on login
 */
export const beginLogoutChain = (
  initiatingApplication?: string | null,
  application?: string | null,
  returnTo?: string | null,
): boolean => {
  if (hopInFlight) {
    return true;
  }

  // Stored before the first hop, because the queue is what survives the
  // navigations and this has to survive them with it.
  try {
    if (returnTo && isAllowedReturnUrl(returnTo)) {
      sessionStorage.setItem(RETURN_KEY, returnTo);
    } else {
      sessionStorage.removeItem(RETURN_KEY);
    }
  } catch {
    // No storage: the chain still runs, it just ends on the login page.
  }

  const initiator = (initiatingApplication ?? '').toLowerCase();

  const keys = LOGOUT_URL_KEYS.filter((key) => {
    if (!isUsableUrl(key)) {
      return false;
    }
    // 'linendeskLogout' -> 'linendesk'
    return key.replace(/Logout$/, '').toLowerCase() !== initiator;
  });

  // Overwrites any queue left behind by a sign-out the user abandoned part-way.
  if (keys.length === 0 || !writeQueue(keys)) {
    return false;
  }

  return advanceLogoutChain(application);
};
